import _ from "lodash";

export type KeyPath = string[];

export interface FieldNode {
    key: string;
    label: string;
    path: KeyPath;
    children?: FieldNode[];
}

/**
 * Joins a key path into the dot-delimited form consumed by the CSV export selection.
 * Example: ["user", "name"] => "user.name".
 */
export function keyPathToDotDelimitedKey(path: KeyPath): string {
    return path.join(".");
}

/**
 * Traverses an object using the provided key path, returning the value or undefined if the path
 * cannot be fully resolved (e.g., missing segments or non-object intermediate values).
 */
export function getValueAtKeyPath(obj: any, keyPath: KeyPath): any {
    try {
        return keyPath.reduce((acc, key) => acc?.[key], obj);
    } catch {
        return undefined;
    }
}

/**
 * Inspects a single record to build a tree of selectable fields.
 * - Arrays are treated as terminal fields (they are stringified on export).
 * - Plain objects are expanded into children to expose nested properties.
 * - Labels are generated from keys (camelCase -> "Camel Case") unless the key already contains
 *   spacing/punctuation, in which case the key is used verbatim.
 */
export function getAvailableFields<T extends object>(
    data: T,
    path?: string[],
): FieldNode[] {
    if (!data) return [];

    const fields: FieldNode[] = [];

    function getPath(key: string): string[] {
        if (path) return path.concat(key);
        return [key];
    }

    function getLabel(key: string): string {
        const ignoreChars = [" ", "-", "_", "."];
        if (ignoreChars.some((c) => key.includes(c))) {
            return key;
        }

        return _.startCase(_.camelCase(key));
    }

    const entries = Object.entries(data);
    for (const [key, value] of entries) {
        if (Array.isArray(value)) {
            fields.push({
                key,
                label: getLabel(key),
                path: getPath(key),
            });
        } else if (typeof value === "object") {
            const field: FieldNode = {
                key,
                label: getLabel(key),
                path: getPath(key),
            };

            const children = getAvailableFields(value, field.path);
            if (children.length > 0) {
                field.children = children;
            }

            fields.push(field);
        } else {
            fields.push({
                key,
                label: getLabel(key),
                path: getPath(key),
            });
        }
    }

    return fields;
}

/**
 * Combines field-node trees from multiple records so that the final tree reflects every field
 * encountered across the dataset. Nodes are deduplicated by key at each level and children are
 * merged recursively.
 */
export function mergeFieldNodes(allFieldNodes: FieldNode[][]): FieldNode[] {
    const fieldNodes: FieldNode[] = [];

    for (const nodes of allFieldNodes) {
        for (const node of nodes) {
            const alreadyTracked = fieldNodes.find((n) => n.key === node.key);
            if (alreadyTracked) {
                if (node.children && node.children.length > 0) {
                    alreadyTracked.children ??= [];
                    alreadyTracked.children = mergeFieldNodes([
                        alreadyTracked.children,
                        node.children,
                    ]);
                }
            } else {
                fieldNodes.push(node);
            }
        }
    }

    return fieldNodes;
}

/**
 * Builds a unified field-node tree for an array of records, ensuring the available selection
 * includes every field present in any item.
 */
export function getAllFieldNodes<T extends object>(data: T[]): FieldNode[] {
    return mergeFieldNodes(data.map((d) => getAvailableFields(d)));
}

/**
 * Produces a lookup from dot-delimited leaf keys to their full key paths. Only leaf nodes are
 * included because those correspond to exportable columns.
 */
export function getKeyToPathMap(fields: FieldNode[]): Map<string, KeyPath> {
    const keyToPath = new Map<string, KeyPath>();

    function traverse(nodes: FieldNode[]) {
        for (const node of nodes) {
            if (node.children) {
                traverse(node.children);
            } else {
                keyToPath.set(keyPathToDotDelimitedKey(node.path), node.path);
            }
        }
    }

    traverse(fields);
    return keyToPath;
}

/**
 * Produces a lookup from dot-delimited leaf keys to their display labels, preserving the label
 * that was derived while walking the object structure.
 */
export function getKeyToLabelMap(fields: FieldNode[]): Map<string, string> {
    const keyToLabel = new Map<string, string>();

    function traverse(nodes: FieldNode[]) {
        for (const node of nodes) {
            if (node.children) {
                traverse(node.children);
            } else {
                keyToLabel.set(keyPathToDotDelimitedKey(node.path), node.label);
            }
        }
    }

    traverse(fields);
    return keyToLabel;
}
